import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FromDesktop } from '../adaptive';
import {
  CarouselControl,
  Container,
  carouselWrapperStyles,
  pageContentStyles,
  titleStyle,
  topLineStyles,
  wrapperStyles,
} from './styles';
import { Layout, Typography } from 'fronton-react';
import { CarouselControlButton } from './carousel-control';
import { scrollToPosition } from 'utils/scroll-to-position';

type CarouselProps = {
  title?: string;
  scrollStep?: number;
  children: React.ReactNode;
  onChange?: (value: number) => void;
};

export const Carousel = ({ title, children, scrollStep, onChange }: CarouselProps) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [inScrollNow, setInScrollNow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const onScroll = ({ target }: Event) => {
    const { scrollLeft } = target as HTMLDivElement;
    if (!inScrollNow && onChange) onChange(scrollLeft);
    checkCanScroll();
  };

  const checkCanScroll = useCallback(() => {
    const element = carouselRef.current;

    if (element) {
      const { scrollLeft, offsetWidth, scrollWidth } = element;

      /**
       * When hide arrow from end of scrolling in px
       */
      const indentation = 100;
      /**
       * Carousel can scroll to left
       * Hide or show left arrow
       */
      if (scrollLeft < indentation) {
        setCanScrollLeft(false);
      } else {
        setCanScrollLeft(true);
      }
      /**
       * Carousel can scroll to right
       * Hide or show right arrow
       */
      if (scrollWidth - offsetWidth - indentation < scrollLeft) {
        setCanScrollRight(false);
      } else {
        setCanScrollRight(true);
      }
    }
  }, []);

  useEffect(() => {
    checkCanScroll();
  }, [checkCanScroll]);

  const onControlClick = useCallback(
    (direction: 'left' | 'right') => {
      const element = carouselRef.current;

      if (element) {
        const { scrollLeft, offsetWidth, firstElementChild } = element;
        const cardWidth = firstElementChild?.clientWidth ?? 280; // Buttons enabled only from desktop.
        const fitsInViewportCount = Math.floor(offsetWidth / cardWidth);
        let itemMargins = 0;

        if (firstElementChild) {
          itemMargins =
            parseFloat(getComputedStyle(firstElementChild).marginLeft) +
            parseFloat(getComputedStyle(firstElementChild).marginRight);
        }

        const evenScrollWidth = (cardWidth + itemMargins) * fitsInViewportCount;
        const step = scrollStep ?? evenScrollWidth;

        setInScrollNow(true);

        let offset = 0;
        if (direction === 'left') {
          offset = scrollLeft - step;
        }
        if (direction === 'right') {
          offset = scrollLeft + step;
        }

        if (onChange) onChange(offset);

        scrollToPosition(
          { x: offset, y: 0 },
          { elementToScroll: element, maxDuration: 650, easingFn: 'easeInOutCubic' }
        ).then(() => setInScrollNow(false));
      }
    },
    [onChange, scrollStep]
  );

  return (
    <Layout className={pageContentStyles}>
      <div className={carouselWrapperStyles}>
        <div className={topLineStyles}>
          <Typography className={titleStyle} variant="h2" as="h2" data-qa="carousel-title">
            {title}
          </Typography>
        </div>

        <div className={wrapperStyles}>
          <FromDesktop>
            {canScrollLeft && (
              <CarouselControl direction="left">
                <CarouselControlButton
                  onClick={() => onControlClick('left')}
                  data-qa="carousel-control-left"
                />
              </CarouselControl>
            )}
          </FromDesktop>

          <Container ref={carouselRef} inScrollNow={inScrollNow} onScroll={onScroll} data-qa="carousel">
            {children}
          </Container>

          <FromDesktop>
            {canScrollRight && (
              <CarouselControl direction="right">
                <CarouselControlButton
                  direction="right"
                  onClick={() => onControlClick('right')}
                  data-qa="carousel-control-right"
                />
              </CarouselControl>
            )}
          </FromDesktop>
        </div>
      </div>
    </Layout>
  );
};
